import bowser from "bowser";
import { logData } from "./aegean_api_handler";
import { notify } from "./notifications";

import { deepFlat } from "../../../marlin/src/utility/helpers";

export {
  flat,
  deepFlat,
  uniq,
  uniqBy,
  getSortByPropSorter,
  downloadFile
} from "../../../marlin/src/utility/helpers";

export {
  formatRequiredInputsIntoSets,
  removeDuplicatesFromNestedArray
} from "../../../marlin/src/utility/data_dictionary";

import { FILTER_TYPES, FILTER_ALIASES } from "./constants";

const notAvailable = "N/A";
const invalid = "invalid";

export const EVENT_TYPE = {
  ERROR: {
    INVALID_FETCH_REQUEST: `${invalid}_fetch_request`,
    BATCH_CREATE: `${invalid}_batch_creation`,
    BATCH_FETCH: `${invalid}_batch_fetch`,
    BASKET_CREATE: `${invalid}_basket_creation`,
    BASKET_UPDATE: `${invalid}_basket_update`,
    BASKET_DELETE: `${invalid}_basket_delete`,
    BATCH_INPUT_CREATE: `${invalid}_batch_input_creation`,
    BATCH_INPUT_UPDATE: `${invalid}_batch_input_update`,
    BATCH_INPUT_DELETE: `${invalid}_batch_input_delete`,
    BATCH_RUN_CREATE: `${invalid}_batch_run_create`,
    EXPLORE_DATA_FETCH: `${invalid}_explore_data_fetch`,
    ADRIATIC_DATA_FETCH: `${invalid}_adriatic_data_fetch`,
    AEGEAN_CONNECTOR_FETCH: `${invalid}_aegean_connector_fetch`,
    TAG_CREATE: `${invalid}_tag_creation`,
    CHANNEL_FETCH: `${invalid}_channel_fetch`,
    CHANNEL_CREATE: `${invalid}_channel_creation`,
    CHANNEL_UPDATE: `${invalid}_channel_update`,
    CHANNEL_DELETE: `${invalid}_channel_delete`,
    ATLANTIC_CONNECTOR_FETCH: `${invalid}_atlantic_connector_fetch`,
    PIPE_DELETE: `${invalid}_pipe_delete`,
    DATA_FUNCTION_CREATE: `${invalid}_data_function_creation`,
    PIPE_UPDATE_WITH_DATA_FUNCTION: `${invalid}_pipe_update_with_data_function`,
    PIPE_DATA_FUNCTION_ASSOCIATION_CREATE: `${invalid}_pipe_data_function_association_creation`,
    PIPE_CREATE: `${invalid}_pipe_creation`,
    PIPE_UPDATE: `${invalid}_pipe_update`,
    PIPE_DUPLICATE: `${invalid}_pipe_duplication`,
    BLACKFIN_LOOKUP: `${invalid}_blackfin_lookup`,
    NEW_DATA_REQUEST_FETCH: `${invalid}_new_data_request_fetch`,
    DOWNLOAD_SAMPLE_DATA: `${invalid}_sample_data_download`,
    PARSE_SAMPLE_DATA: `${invalid}_sample_data_parse`,
    TAG_DELETE: `${invalid}_tag_delete`,
    TABLE_PROVIDER_TAG_ASSOCIATION_DELETE: `${invalid}_table_provider_tag_association_delete`,
    UNKNOWN_PLAN: `${invalid}_unknown_plan`,
    BATCH_RESULTS_GET: `${invalid}_batch_results`,
    DATA_FUNCTION_FETCH: `${invalid}_data_function_fetch`,
    LOGGING_OUT: `${invalid}_logging_out_user`,
    WORKSPACE_CREATE: `${invalid}_workspace_creation`,
    WORKSPACE_UPDATE: `${invalid}_workspace_update`,
    WORKSPACE_DATA_FETCH: `${invalid}_workspace_fetch`,
    WORKSPACE_ADD_USERS: `${invalid}_workspace_add_users`,
    WORKSPACE_PROVIDERS_DELETE: `${invalid}_workspace_providers_deletion`,
    WORKSPACE_PROVIDERS_ADD: `${invalid}_workspace_add_providers`,
    RELEASE_NOTES_FETCH: `${invalid}_release_notes_fetch`,
    TABLE_PROVIDERS_OUTPUT_FIELDS_FETCH: `${invalid}_table_providers_output_fields_fetch`,
    TABLE_PROVIDER_OUTPUT_FIELDS_FETCH: `${invalid}_single_table_provider_output_fields_fetch`,
    POLLING_NOTIFICATION: `${invalid}_notification_fetch`,
    BOOKMARK_DELETE: `${invalid}_bookmark_delete`,
    BOOKMARK_CREATE: `${invalid}_bookmark_create`,
    REACT_RENDER: `${invalid}_react_render`
  },
  INFO: {
    PRODUCT_MORE_INFO_CLICKED: "product_more_info_clicked",
    PRODUCT_ADDED_TO_BASKET: "product_added_to_basket",
    PRODUCT_REMOVED_FROM_BASKET: "product_removed_from_basket",
    PRODUCT_SAMPLE_DATA_DOWNLOADED: "product_sample_data_downloaded",
    PRODUCT_ADDED_TO_WORKSPACE_FROM_LABS:
      "product_added_to_workspace_from_labs",
    BATCH_INPUT_FILE_CREATED: "batch_input_file_created",
    BASKET_CREATED: "basket_created",
    BATCH_RUN_CREATED: "batch_run_created",
    BATCH_EXPORT_REQUESTED: "batch_export_requested"
  }
};

export const logInteraction = (message, event, data = {}) => {
  console.info(message);
  logData("info", event, Object.assign({}, data, { message }))
    .then(res => console.log("Success: log sent", event, res))
    .catch(e => console.error("Failed logging to manta", e));
};

// TODO: REFACTOR ALL THIS ERROR HANDLING - ESPECIALLY IN `aegean_api_handler`. It's ugly as hell
export const handleError = (
  message,
  err,
  event = EVENT_TYPE.ERROR.INVALID_FETCH_REQUEST,
  extraData = {},
  type = "error"
) => {
  if (err && err.already_handled) {
    // if we see this exact same error object again, do not put up another alert for it
    return false;
  } else {
    err.already_handled = true;
  }

  // console.warn("[DEBUG] Error in handleError: ", err);
  const errorMessage = (
    err.message
    || (
      err.body
      && (
        err.body.response
        || err.body.errors
        || err.body.error
      )
    )
  );

  let messagesToDisplay = null;
  if (message == errorMessage) {
    messagesToDisplay = [message].filter(Boolean);
  } else {
    messagesToDisplay = [message].concat(errorMessage).filter(Boolean);
  }

  notify(messagesToDisplay, type);

  const browserInfo = {
    browserName: bowser.name || notAvailable,
    browserVersion: bowser.version || notAvailable,
    isMobile: bowser.mobile || false,
    isTablet: bowser.tablet || false,
    operatingSystem: bowser.osname || notAvailable,
    operatingSystemVersion: bowser.osversion || notAvailable
  };

  const data = {
    message,
    ...extraData,
    ...browserInfo
  };

  const customError = {
    error_message: err.message,
    error_stack: err.stack,
    ...data
  };

  logData(type, event, customError)
    .then(res => console.log("Success: log sent", event, res))
    .catch(e => console.error("Failed logging to manta", e));
};

export const getInitials = (name = "") => name && name.split(/\s/).map(w => w[0]).join("");

export const logBatchDownload = (
  providerOrVersionName,
  providerOrVersionId,
  batchName,
  batchId
) => {
  logInteraction(
    "Batch Run Results Downloaded",
    "batch_run_results_downloaded",
    {
      aegean_batch_run_name: batchName,
      aegean_batch_run_id: batchId,
      provider_name: providerOrVersionName,
      most_recent_export_id: providerOrVersionId
    }
  );
  return true;
};

export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1);

export const getDuplicates = arr => arr.filter((uniqHeader, index) => arr.lastIndexOf(uniqHeader) !== index);

export const getFiltersMapFromUrl = search => Object.keys(search).reduce((urlFilters, filterKey) => {
  if (Object.values(FILTER_TYPES).includes(filterKey)) {
    urlFilters[filterKey] = search[filterKey];
  }
  return urlFilters;
}, {});

export const isObject = obj => typeof obj === "object" && obj !== null;

export const getUniqueInputNames = (providers) => {
  const inputs = providers.reduce((inputs, provider) => {
    const getInputName = input => input.name;
    const requiredInputNames = deepFlat(provider.version.required_inputs).map(getInputName);
    const allInputNames = provider.version.input_fields.map(getInputName);
    const optionalInputNames = allInputNames.filter(input => !requiredInputNames.includes(input.name));

    inputs.requiredInputNames.push(...requiredInputNames);
    inputs.optionalInputNames.push(...optionalInputNames);

    return inputs;
  }, {
    requiredInputNames: [],
    optionalInputNames: []
  });

  const uniqueRequiredInputNames = Array.from(new Set(inputs.requiredInputNames));
  const filteredUniqueOptionalInputNames = Array.from(new Set(inputs.optionalInputNames)).filter((optionalInput) => {
    const isOptionalInputinRequiredInputs = uniqueRequiredInputNames.includes(optionalInput);

    return !isOptionalInputinRequiredInputs;
  });

  return {
    uniqueRequiredInputNames,
    uniqueOptionalInputNames: filteredUniqueOptionalInputNames
  };
};

export const getTopLevelCategory = (categoryId, customCategories) => {
  const filteredCategory = customCategories.find(
    category => category.id == categoryId
  );

  const getTopMostCategory = (foundCategory) => {
    if (!foundCategory) {
      return foundCategory;
    }

    const parent = customCategories.find(category => category.id === foundCategory.parent_id);

    return getTopMostCategory(parent) || foundCategory;
  };

  return getTopMostCategory(filteredCategory);
};

export const formatFiltersForChips = (filters, customCategories) =>
  Object.keys(filters).reduce((activeFilters, filterKey) => {
    const statePropValue = filters[filterKey];
    if (Array.isArray(statePropValue)) {
      statePropValue.length && statePropValue.forEach((statePropItem) => {
        const base = {
          parent: filterKey,
          label: ""
        };
        const itemValue = statePropItem.name;
        const itemId = statePropItem.id;
        if (filterKey === FILTER_TYPES.SELECTED_CATEGORIES) {
          const topLevelCategory = getTopLevelCategory(itemId, customCategories);
          const categoryChip = activeFilters.find(chip => chip.value === topLevelCategory.name);
          if (categoryChip) {
            const newCategories = {
              categories: [
                ...categoryChip.categories,
                {
                  value: itemValue,
                  id: itemId
                }
              ]
            };
            const newCategoryChip = Object.assign({}, categoryChip, newCategories);
            activeFilters = activeFilters.map(chip => chip.value === topLevelCategory.name ? newCategoryChip : chip);
          } else {
            activeFilters.push(
              {
                parent: filterKey,
                value: topLevelCategory.name,
                categories: [{
                  value: itemValue,
                  id: itemId
                }]
              }
            );
          }
        } else if (isObject(statePropItem)) {
          const mergedChipWithId = Object.assign({}, base, {
            id: itemId,
            value: itemValue
          });
          activeFilters.push(mergedChipWithId);
        } else {
          const mergedChip = Object.assign({}, base, {
            value: statePropItem
          });
          activeFilters.push(mergedChip);
        }
      });
    } else if (statePropValue) {
      activeFilters.push({
        parent: filterKey,
        label: FILTER_ALIASES[filterKey] || statePropValue,
        value: statePropValue
      });
    }
    return activeFilters;
  }, []);
